import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, {
  ClearIndicatorProps,
  DropdownIndicatorProps,
  components as reactSelectComponents,
} from "react-select";
import countryList from "react-select-country-list";

import { AlertTriangle, CheckCircle2, ChevronDown, Info, Loader, X } from "lucide-react";

import { api } from "../utils/apiClient";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/Dialog";

interface BusinessProfile {
  business_name: string | null;
  business_domain: string | null;
  business_category: string | null;
  city: string | null;
  country: string | null;
  neighbourhood?: string | null;
  price_tier?: string | null;
  amenities?: string[];
  email?: string;
  phone_number?: string | null;
  is_complete: boolean;
}

const PRICE_TIERS = ["Budget", "Economy", "Mid-range", "Upscale", "Luxury"];

interface CountryOption {
  value: string;
  label: string;
}

interface BusinessProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Domain to prefill when the user has no saved business domain (e.g. from their last audit). */
  prefillDomain?: string;
  /** Called with the updated profile after a successful save. */
  onSaved?: (profile: BusinessProfile) => void;
  /**
   * When true the profile is required: the dialog cannot be dismissed and all fields must be filled
   * before it closes. Used when the user has not yet completed their business profile.
   */
  mandatory?: boolean;
}

interface FormState {
  businessName: string;
  businessDomain: string;
  businessCategory: string;
  city: string;
  country: string;
  neighbourhood: string;
  priceTier: string;
  amenities: string;
}

const EMPTY_FORM: FormState = {
  businessName: "",
  businessDomain: "",
  businessCategory: "",
  city: "",
  country: "",
  neighbourhood: "",
  priceTier: "",
  amenities: "",
};

const inputClasses =
  "w-full bg-surface-container-low border-none rounded-xl px-5 py-3.5 text-on-surface text-sm placeholder:text-outline/50 focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition-all outline-none";

const selectClasses = `${inputClasses} appearance-none pr-10 cursor-pointer`;

const SelectChevron = () => (
  <ChevronDown className="w-4 h-4 text-outline pointer-events-none absolute right-5 top-1/2 -translate-y-1/2" />
);

const BusinessCategoryTooltip = () => (
  <div
    role="tooltip"
    id="businessCategoryTooltip"
    className="absolute top-[22px] right-0 mt-1 z-50 w-64 max-w-[80vw] rounded-lg bg-gray-900 p-3 text-xs text-white shadow-xl opacity-0 pointer-events-none transition-opacity duration-150 group-hover/info:opacity-100 group-focus/info:opacity-100 normal-case tracking-normal font-normal"
  >
    <p className="text-gray-300">
      Describe your business in a few words — e.g.{" "}
      <span className="font-semibold">boutique hotel</span>,{" "}
      <span className="font-semibold">personal injury law firm</span>,{" "}
      <span className="font-semibold">SaaS marketing agency</span>,{" "}
      <span className="font-semibold">artisan bakery</span>. This helps us tailor AI visibility and
      audit results to your industry.
    </p>
  </div>
);

const CountryDropdownIndicator = (props: DropdownIndicatorProps<CountryOption, false>) => (
  <reactSelectComponents.DropdownIndicator {...props}>
    <ChevronDown className="w-4 h-4" />
  </reactSelectComponents.DropdownIndicator>
);

const CountryClearIndicator = (props: ClearIndicatorProps<CountryOption, false>) => (
  <reactSelectComponents.ClearIndicator {...props}>
    <X className="w-4 h-4" />
  </reactSelectComponents.ClearIndicator>
);

const BusinessProfileDialog = ({
  open,
  onOpenChange,
  prefillDomain,
  onSaved,
  mandatory = false,
}: BusinessProfileDialogProps) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUnsaved, setShowUnsaved] = useState(false);

  // Snapshot of the values as loaded, used to detect unsaved changes.
  const initialRef = useRef<FormState>(EMPTY_FORM);
  const savedRef = useRef(false);

  const countryOptions = useMemo(() => countryList().getData() as CountryOption[], []);
  const selectedCountry = countryOptions.find((opt) => opt.label === form.country) ?? null;

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialRef.current);
  const isValid =
    form.businessName.trim() !== "" &&
    form.businessDomain.trim() !== "" &&
    form.businessCategory.trim() !== "" &&
    form.city.trim() !== "" &&
    form.country.trim() !== "";

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const profileRes = await api.get("/user/profile");

      const profile: BusinessProfile = profileRes.data;
      const next: FormState = {
        businessName: profile.business_name || "",
        businessDomain: (profile.business_domain || prefillDomain || "").replace(
          /^https?:\/\//i,
          "",
        ),
        businessCategory: profile.business_category || "",
        city: profile.city || "",
        country: profile.country || "",
        neighbourhood: profile.neighbourhood || "",
        priceTier: profile.price_tier || "",
        amenities: (profile.amenities || []).join(", "),
      };
      initialRef.current = next;
      setForm(next);
    } catch {
      // Fall back to an empty form prefilled with the last-audit domain.
      const next: FormState = {
        ...EMPTY_FORM,
        businessDomain: (prefillDomain || "").replace(/^https?:\/\//i, ""),
      };
      initialRef.current = next;
      setForm(next);
      setError("Could not load your saved profile. You can still add your details below.");
    } finally {
      setLoading(false);
    }
  }, [prefillDomain]);

  useEffect(() => {
    if (open) {
      savedRef.current = false;
      setShowUnsaved(false);
      loadProfile();
    }
  }, [open, loadProfile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const close = () => {
    setShowUnsaved(false);
    onOpenChange(false);
  };

  // Intercept Radix close requests (Esc, overlay click, X button).
  const requestClose = () => {
    // A mandatory (incomplete) profile cannot be dismissed.
    if (mandatory) {
      return;
    }
    if (isDirty && !savedRef.current) {
      setShowUnsaved(true);
      return;
    }
    close();
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isValid) {
      setError("Please fill in all fields to continue.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const rawDomain = form.businessDomain.trim();
      const fullDomain =
        rawDomain && !/^https?:\/\//i.test(rawDomain) ? `https://${rawDomain}` : rawDomain;
      const res = await api.post("/user/profile", {
        business_name: form.businessName.trim(),
        business_domain: fullDomain,
        business_category: form.businessCategory.trim(),
        city: form.city.trim(),
        country: form.country,
        neighbourhood: form.neighbourhood.trim() || null,
        price_tier: form.priceTier.trim() || null,
        amenities: form.amenities
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean),
      });
      savedRef.current = true;
      initialRef.current = form;
      if (onSaved) onSaved(res.data);
      setShowUnsaved(false);
      onOpenChange(false);
    } catch (err) {
      const e2 = err as { response?: { data?: { detail?: string } } };
      setError(
        typeof e2.response?.data?.detail === "string"
          ? e2.response.data.detail
          : "Failed to save your business profile. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        hideClose={mandatory}
        onEscapeKeyDown={(e) => {
          if (mandatory) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (mandatory) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (mandatory) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Your Business Profile</DialogTitle>
          <DialogDescription>
            {mandatory
              ? "Tell us about your business to continue. These details help us tailor audits and visibility results to you."
              : "Tell us about your business so we can tailor audits and visibility results to you."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-on-surface-variant">
              <Loader className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label
                    htmlFor="businessName"
                    className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                  >
                    Business Name <span className="text-error">*</span>
                  </label>
                  <input
                    id="businessName"
                    type="text"
                    name="businessName"
                    value={form.businessName}
                    onChange={handleChange}
                    required
                    placeholder="Acme Resorts"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label
                    htmlFor="businessDomain"
                    className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                  >
                    Business Domain <span className="text-error">*</span>
                  </label>
                  <input
                    id="businessDomain"
                    type="text"
                    name="businessDomain"
                    value={form.businessDomain}
                    onChange={handleChange}
                    required
                    placeholder="acmeresorts.com"
                    className={inputClasses}
                  />
                </div>

                <div className="relative">
                  <label
                    htmlFor="businessCategory"
                    className="w-fit flex items-center gap-1 text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                  >
                    Business Category <span className="text-error">*</span>
                    <button
                      type="button"
                      className="group/info inline-flex rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      aria-label="What is a business category?"
                      aria-describedby="businessCategoryTooltip"
                    >
                      <Info
                        className="w-3 h-3 text-on-surface-variant opacity-60"
                        aria-hidden="true"
                      />
                      <BusinessCategoryTooltip />
                    </button>
                  </label>
                  <input
                    id="businessCategory"
                    type="text"
                    name="businessCategory"
                    value={form.businessCategory}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    placeholder='e.g. "boutique hotel", "law firm", "marketing agency"'
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label
                    htmlFor="country"
                    className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                  >
                    Country <span className="text-error">*</span>
                  </label>
                  <Select<CountryOption>
                    inputId="country"
                    name="country"
                    options={countryOptions}
                    value={selectedCountry}
                    onChange={(option) =>
                      setForm((prev) => ({ ...prev, country: option ? option.label : "" }))
                    }
                    placeholder="Select a country…"
                    isClearable
                    unstyled
                    maxMenuHeight={176}
                    components={{
                      DropdownIndicator: CountryDropdownIndicator,
                      ClearIndicator: CountryClearIndicator,
                    }}
                    styles={{
                      control: (base) => ({ ...base, minHeight: "auto" }),
                    }}
                    classNames={{
                      control: ({ isFocused }) =>
                        [
                          "w-full bg-surface-container-low rounded-xl px-5 py-3.5 text-on-surface text-sm cursor-pointer transition-all",
                          isFocused ? "ring-2 ring-primary/20 bg-surface-container-lowest" : "",
                        ].join(" "),
                      placeholder: () => "text-outline/50",
                      input: () => "text-on-surface",
                      singleValue: () => "text-on-surface",
                      menu: () =>
                        "mt-1 bg-surface-container-lowest border border-outline/10 rounded-xl overflow-hidden shadow-lg z-50",
                      menuList: () => "max-h-44",
                      option: ({ isFocused, isSelected }) =>
                        [
                          "px-4 py-2 text-sm cursor-pointer text-on-surface",
                          isFocused ? "bg-surface-container-high" : "",
                          isSelected ? "bg-primary/10 font-semibold" : "",
                        ].join(" "),
                      dropdownIndicator: () => "text-outline px-1",
                      clearIndicator: () => "text-outline px-1 cursor-pointer",
                      indicatorSeparator: () => "hidden",
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="city"
                    className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                  >
                    City <span className="text-error">*</span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    required
                    placeholder="Mumbai"
                    className={inputClasses}
                  />
                </div>
              </div>

              <div className="pt-1">
                <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-wider mb-2">
                  Optional — sharpens Persona Visibility scans
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="priceTier"
                      className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                    >
                      Price Tier
                    </label>
                    <div className="relative">
                      <select
                        id="priceTier"
                        name="priceTier"
                        value={form.priceTier}
                        onChange={handleChange}
                        className={selectClasses}
                      >
                        <option value="">Not specified</option>
                        {PRICE_TIERS.map((tier) => (
                          <option key={tier} value={tier}>
                            {tier}
                          </option>
                        ))}
                      </select>
                      <SelectChevron />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="neighbourhood"
                      className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                    >
                      Neighbourhood
                    </label>
                    <input
                      id="neighbourhood"
                      type="text"
                      name="neighbourhood"
                      value={form.neighbourhood}
                      onChange={handleChange}
                      placeholder="Le Marais"
                      className={inputClasses}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label
                    htmlFor="amenities"
                    className="block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider"
                  >
                    Amenities <span className="lowercase font-normal">(comma-separated)</span>
                  </label>
                  <input
                    id="amenities"
                    type="text"
                    name="amenities"
                    value={form.amenities}
                    onChange={handleChange}
                    placeholder="spa, pool, gym, pet-friendly"
                    className={inputClasses}
                  />
                </div>
              </div>

              {error && <p className="text-error text-sm font-medium">{error}</p>}

              {showUnsaved ? (
                <div className="mt-2 rounded-xl bg-surface-container-low p-4">
                  <div className="flex items-start gap-2.5 text-on-surface">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-yellow-600 shrink-0" />
                    <p className="text-sm">You have unsaved changes. Save them before closing?</p>
                  </div>
                  <div className="flex gap-3 mt-3">
                    <button
                      type="button"
                      onClick={close}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors"
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSubmit()}
                      disabled={saving || !isValid}
                      className="flex-1 bg-gradient-to-br from-primary to-primary-container text-on-primary py-2.5 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {saving ? <Loader className="w-4 h-4 animate-spin" /> : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={saving || !isValid}
                  className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Save
                    </>
                  )}
                </button>
              )}
            </form>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessProfileDialog;
